import { BaseFeature } from '../../core/base-feature.js';

/**
 * EyeLockFeature - Frontend feature for Eye Lock challenge
 * Allows admin to upload a photo, set a target area, and start an eye-tracking
 * stare challenge on the desktop.
 *
 * Enhancements:
 * - Heatmap gaze point accumulation
 * - Queue/playlist management
 * - Session history & stats fetching
 * - Shame capture storage
 * - Mid-session controls (relocate target, distractions)
 * - Heatmap replay with full point history
 * - Configurable queue delay
 */
export class EyeLockFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Session state
        this.sessionState = {
            state: 'idle',
            gazeState: 'waiting',
            gazeMode: 'eye_tracker',
            sessionId: null,
            remainingMs: 0,
            totalDurationMs: 0,
            progress: 0,
            lookAwayCount: 0,
            penaltyAccumulatedMs: 0,
            config: null,
            heatmapPoints: [],
            allGazePoints: [],
            shameCaptures: [],
            distanceFromTarget: 0,
            currentStreakMs: 0
        };

        // Queue/playlist state
        this.queue = [];
        this.queueIndex = 0;
        this.queueMode = false;
        this.queueDelay = 2000; // Configurable queue delay (#16)

        // History/stats cache
        this.history = null;
        this.stats = null;

        // Event callbacks for React components
        this.stateUpdateCallbacks = new Set();
        this.completionCallbacks = new Set();
        this.updateCallbacks = new Set();
        this.queueProgressCallbacks = new Set();
    }

    static getMetadata() {
        return {
            id: 'eye-lock',
            name: 'Eye Lock',
            version: 'v1.0',
            tier: 'external',
            capabilities: [
                'start',
                'cancel',
                'get_state',
                'get_history',
                'get_stats',
                'relocate_target',
                'inject_distraction'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[EyeLock] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                return this.handleStateUpdate(payload);
            case 'initial_state':
                return this.handleInitialState(payload);
            case 'shame_capture':
                return this._handleShameCapture(payload);
            default:
                console.warn(`[EyeLock] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleStateUpdate(payload) {
        switch (payload.type) {
            case 'session_started':
                this.sessionState = {
                    ...this.sessionState,
                    state: 'active',
                    gazeState: 'waiting',
                    gazeMode: payload.gazeMode || 'eye_tracker',
                    sessionId: payload.sessionId,
                    totalDurationMs: payload.durationSeconds * 1000,
                    remainingMs: payload.durationSeconds * 1000,
                    progress: 0,
                    lookAwayCount: 0,
                    penaltyAccumulatedMs: 0,
                    heatmapPoints: [],
                    allGazePoints: [],
                    shameCaptures: [],
                    distanceFromTarget: 0,
                    currentStreakMs: 0
                };
                break;

            case 'progress':
                this.sessionState = {
                    ...this.sessionState,
                    gazeState: payload.gazeState,
                    remainingMs: payload.remainingMs,
                    progress: payload.progress,
                    lookAwayCount: payload.lookAwayCount,
                    penaltyAccumulatedMs: payload.penaltyAccumulatedMs,
                    distanceFromTarget: payload.distanceFromTarget || 0,
                    currentStreakMs: payload.currentStreakMs || 0
                };
                // Accumulate heatmap gaze points — no cap during active session (#3 replay)
                if (payload.gazePoints && payload.gazePoints.length > 0) {
                    // allGazePoints: unlimited for replay
                    this.sessionState.allGazePoints = [
                        ...this.sessionState.allGazePoints,
                        ...payload.gazePoints
                    ];
                    // heatmapPoints: capped for live display
                    const combined = [...this.sessionState.heatmapPoints, ...payload.gazePoints];
                    this.sessionState.heatmapPoints = combined.length > 500
                        ? combined.slice(-500) : combined;
                }
                break;

            case 'look_away':
                this.sessionState = {
                    ...this.sessionState,
                    gazeState: 'away',
                    lookAwayCount: payload.lookAwayCount,
                    remainingMs: payload.remainingMs,
                    penaltyAccumulatedMs: payload.penaltyAccumulatedMs,
                    distanceFromTarget: payload.distanceFromTarget || 0,
                    currentStreakMs: 0
                };
                break;

            case 'resumed':
                this.sessionState = {
                    ...this.sessionState,
                    gazeState: 'staring',
                    remainingMs: payload.remainingMs
                };
                break;

            case 'session_completed':
                this.sessionState = {
                    ...this.sessionState,
                    state: 'completed',
                    gazeState: 'waiting',
                    remainingMs: 0,
                    progress: 1
                };
                this.notifyCompletionListeners(payload);
                this._handleQueueAdvance();
                break;

            case 'session_cancelled':
                this.sessionState = {
                    ...this.sessionState,
                    state: 'cancelled',
                    gazeState: 'waiting'
                };
                this.notifyCompletionListeners(payload);
                this._handleQueueAdvance();
                break;

            case 'fake_complete':
                // Don't transition to completed — session continues
                // Just notify UI for visual feedback
                break;

            case 'target_relocated':
            case 'distraction_injected':
                // Informational — no state change needed
                break;
        }

        this.notifyStateUpdateListeners(payload);
        this.notifyUpdateListeners();
    }

    handleInitialState(payload) {
        if (payload && payload.state) {
            this.sessionState = {
                ...this.sessionState,
                ...payload.state
            };
        }
        this.notifyUpdateListeners();
    }

    _handleShameCapture(payload) {
        const captures = [...this.sessionState.shameCaptures, payload];
        this.sessionState.shameCaptures = captures.length > 10 ? captures.slice(-10) : captures;
        this.notifyUpdateListeners();
    }

    // =====================================
    // Queue/Playlist
    // =====================================

    addToQueue(item) {
        // item: { mediaData, mediaType, targetArea, durationSeconds, ...config }
        this.queue.push(item);
        this.notifyUpdateListeners();
    }

    removeFromQueue(index) {
        this.queue.splice(index, 1);
        this.notifyUpdateListeners();
    }

    setQueueDelay(ms) {
        this.queueDelay = Math.max(0, Math.min(30000, ms));
    }

    startQueue() {
        if (this.queue.length === 0) return;
        this.queueMode = true;
        this.queueIndex = 0;
        this.notifyQueueProgressListeners();
        this._startQueueItem(0);
    }

    _startQueueItem(index) {
        if (index >= this.queue.length) {
            this.queueMode = false;
            this.notifyQueueProgressListeners();
            this.notifyUpdateListeners();
            return;
        }
        this.queueIndex = index;
        this.notifyQueueProgressListeners();
        this.start(this.queue[index]);
    }

    _handleQueueAdvance() {
        if (!this.queueMode) return;
        const nextIndex = this.queueIndex + 1;
        if (nextIndex >= this.queue.length) {
            this.queueMode = false;
            this.notifyQueueProgressListeners();
            return;
        }
        // Wait configurable delay then start next
        setTimeout(() => {
            this._startQueueItem(nextIndex);
        }, this.queueDelay);
    }

    // =====================================
    // History & Stats
    // =====================================

    async fetchHistory() {
        return new Promise((resolve) => {
            const messageId = 'hist_' + Date.now();
            const handler = (msg) => {
                if (msg.action === 'get_history' || msg.cmd === 'get_history') {
                    this.history = msg.payload?.sessions || [];
                    this.notifyUpdateListeners();
                    resolve(this.history);
                }
            };
            // Use one-shot response listener
            this.stateUpdateCallbacks.add(handler);
            setTimeout(() => this.stateUpdateCallbacks.delete(handler), 5000);
            this.sendMessage('get_history', {});
        });
    }

    async fetchStats() {
        return new Promise((resolve) => {
            const handler = (msg) => {
                if (msg.action === 'get_stats' || msg.cmd === 'get_stats') {
                    this.stats = msg.payload?.stats || null;
                    this.notifyUpdateListeners();
                    resolve(this.stats);
                }
            };
            this.stateUpdateCallbacks.add(handler);
            setTimeout(() => this.stateUpdateCallbacks.delete(handler), 5000);
            this.sendMessage('get_stats', {});
        });
    }

    // =====================================
    // Command Methods
    // =====================================

    async start(config) {
        this.sendMessage('start', config);
    }

    async cancel() {
        this.sendMessage('cancel', {});
    }

    async getSessionState() {
        this.sendMessage('get_state', {});
    }

    // Feature 7: Mid-session target relocation
    async relocateTarget(targetArea) {
        this.sendMessage('relocate_target', targetArea);
    }

    // Feature 11: Distraction injection
    async injectDistraction(type, data) {
        this.sendMessage('inject_distraction', { type, data });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onStateUpdate(callback) {
        this.stateUpdateCallbacks.add(callback);
        return () => this.stateUpdateCallbacks.delete(callback);
    }

    onCompletion(callback) {
        this.completionCallbacks.add(callback);
        return () => this.completionCallbacks.delete(callback);
    }

    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        // Fire once synchronously with the cached state so views relying on
        // this event to clear their loading flag don't wait forever when the
        // desktop has no live session to push (and no broadcastInitialState
        // wiring in peer-manager).
        try {
            callback({
                state: this.sessionState,
                queue: this.queue,
                queueIndex: this.queueIndex,
                queueMode: this.queueMode,
                queueDelay: this.queueDelay,
                history: this.history,
                stats: this.stats
            });
        } catch (e) {
            console.error('[EyeLock] Update callback error on subscribe:', e);
        }
        return () => this.updateCallbacks.delete(callback);
    }

    onQueueProgress(callback) {
        this.queueProgressCallbacks.add(callback);
        return () => this.queueProgressCallbacks.delete(callback);
    }

    notifyStateUpdateListeners(data) {
        for (const cb of this.stateUpdateCallbacks) {
            try { cb(data); } catch (e) { console.error('[EyeLock] State update callback error:', e); }
        }
    }

    notifyCompletionListeners(data) {
        for (const cb of this.completionCallbacks) {
            try { cb(data); } catch (e) { console.error('[EyeLock] Completion callback error:', e); }
        }
    }

    notifyUpdateListeners() {
        const data = {
            state: this.sessionState,
            queue: this.queue,
            queueIndex: this.queueIndex,
            queueMode: this.queueMode,
            queueDelay: this.queueDelay,
            history: this.history,
            stats: this.stats
        };
        for (const cb of this.updateCallbacks) {
            try { cb(data); } catch (e) { console.error('[EyeLock] Update callback error:', e); }
        }
    }

    notifyQueueProgressListeners() {
        const data = {
            queueMode: this.queueMode,
            queueIndex: this.queueIndex,
            queueLength: this.queue.length
        };
        for (const cb of this.queueProgressCallbacks) {
            try { cb(data); } catch (e) { console.error('[EyeLock] Queue progress callback error:', e); }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getState() { return this.sessionState; }
    isActive() { return this.sessionState.state === 'active'; }
    getQueue() { return this.queue; }
    isQueueMode() { return this.queueMode; }

    async cleanup() {
        console.log('[EyeLock] Cleaning up...');
        this.sessionState = {
            state: 'idle', gazeState: 'waiting', gazeMode: 'eye_tracker',
            sessionId: null, remainingMs: 0, totalDurationMs: 0, progress: 0,
            lookAwayCount: 0, penaltyAccumulatedMs: 0, config: null,
            heatmapPoints: [], allGazePoints: [], shameCaptures: [], distanceFromTarget: 0,
            currentStreakMs: 0
        };
        this.queue = [];
        this.queueIndex = 0;
        this.queueMode = false;
        this.queueDelay = 2000;
        this.history = null;
        this.stats = null;
        this.stateUpdateCallbacks.clear();
        this.completionCallbacks.clear();
        this.updateCallbacks.clear();
        this.queueProgressCallbacks.clear();
    }
}
